import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { collection, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import { syncSubjectsWithStudents } from './SyncSubjects';

const AddSubjectsToClassModal = ({ classId, isOpen, onClose, onSuccess }) => {
  const [allSubjects, setAllSubjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [existingSubjects, setExistingSubjects] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loading1, setLoading1] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch all subjects
      const subjectSnap = await getDocs(collection(db, 'subjects'));
      const subjects = subjectSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllSubjects(subjects);

      // Fetch current class data
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      if (classSnap.exists()) {
        const classData = classSnap.data();
        setExistingSubjects(classData.subjects || []);
      }
    };

    if (isOpen) {
      fetchData();
      setSelectedIds([]); // Reset on open
    }
  }, [classId, isOpen]);

  const handleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleAddSelected = async () => {
    setLoading(true);
    try {
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) return;

      const classData = classSnap.data();
      const currentSubjects = classData.subjects || [];

      const newSubjects = selectedIds.map(id => ({ id, teacherId: '' }));

      const merged = [...currentSubjects, ...newSubjects];
      const uniqueSubjects = Array.from(new Map(merged.map(sub => [sub.id, sub])).values());

      await updateDoc(classRef, { subjects: uniqueSubjects });
      await syncSubjectsWithStudents(classId);

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error adding subjects:", err);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handleRemoveSelected = async () => {
    setLoading1(true);
    try {
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) return;
  
      const classData = classSnap.data();
      const currentSubjects = classData.subjects || [];
  
      // Get the subjects that will be removed
      const removedSubjects = currentSubjects.filter(sub => selectedIds.includes(sub.id));
      
      // Update the class first
      const updatedSubjects = currentSubjects.filter(sub => !selectedIds.includes(sub.id));
      await updateDoc(classRef, { subjects: updatedSubjects });
  
      // Now remove these subjects from all teachers' assignments
      const teachersSnap = await getDocs(collection(db, 'users'));
      const teachers = teachersSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => user.status === 'Teacher' && user.isActive === true);
  
      const updatePromises = teachers.map(async (teacher) => {
        const teacherRef = doc(db, 'users', teacher.id);
        const currentAssignments = teacher.teachingAssignments || [];
        
        // Filter out assignments that match any of the removed subjects for this class
        const updatedAssignments = currentAssignments.filter(assignment => 
          !(assignment.classId === classId && selectedIds.includes(assignment.subjectId))
        );
        
        if (updatedAssignments.length !== currentAssignments.length) {
          await updateDoc(teacherRef, { teachingAssignments: updatedAssignments });
        }
      });
  
      await Promise.all(updatePromises);
      await syncSubjectsWithStudents(classId);
  
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error removing subjects:", err);
    } finally {
      setLoading1(false);
      onClose();
    }
  };

  const isAlreadyAdded = (subjectId) =>
    existingSubjects.some(sub => sub.id === subjectId);

  // Sort subjects: already added ones at the top
  const sortedSubjects = [
    ...allSubjects.filter(sub => isAlreadyAdded(sub.id)),
    ...allSubjects.filter(sub => !isAlreadyAdded(sub.id))
  ];

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}
      className="custom-modal"
      overlayClassName="custom-modal-overlay"
    >
      <p className="size5 caps">Select Subjects</p>
      <input
        type="text"
        placeholder="Search by name"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        className="input-field1"
      />
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {sortedSubjects
            .filter(sub => sub.name?.toLowerCase().includes(searchText.toLowerCase()))
            .map(sub => (
          <div key={sub.id}>
            <label>
              <input
                type="checkbox"
                className="input-checkbox gold-tick male-female-stat"
                checked={selectedIds.includes(sub.id)}
                onChange={() => handleSelect(sub.id)}
              />
              {sub.name} {isAlreadyAdded(sub.id) && <span style={{ color: 'green', fontSize: '0.7rem', }}>(Added)</span>}
            </label>
          </div>
        ))}
      </div>

      <div className="row align-center gap-1">
        <button className="login-button" onClick={onClose}>Cancel</button>
        <button className="login-button" onClick={handleRemoveSelected} disabled={loading1}>{loading1 ? 'Removing...' : 'Remove'}</button>
        <button className="login-button" onClick={handleAddSelected} disabled={loading}>{loading ? 'Adding...' : 'Add'}</button>
      </div>
    </Modal>
  );
};

export default AddSubjectsToClassModal;
